import React from 'react';
import Banner from '../common/Banner';
import type { AccountSubscription } from '../../types/permissions';
import { DEFAULT_AGENCY_TIMEZONE } from '../../types/permissions';
import { BILLING_DISABLED_TITLE, TRIAL_UPGRADE_PROMPT_DAYS_LEFT, formatAccountDate } from '../../utils/subscription';

export type SubscriptionBannerKind = 'read_only' | 'past_due' | 'cancel_scheduled' | 'trial';

/**
 * Which account banner applies (Phase 13, plan §4.1), most urgent first. Suspended and canceled
 * accounts get the /account/suspended page instead of a banner.
 */
export function subscriptionBannerKind(subscription: AccountSubscription | null | undefined): SubscriptionBannerKind | null {
  if (!subscription) return null;
  switch (subscription.status) {
    case 'read_only':
      return 'read_only';
    case 'past_due':
      return 'past_due';
    case 'trialing':
    case 'active':
      if (subscription.cancelAtPeriodEnd) return 'cancel_scheduled';
      return subscription.status === 'trialing' ? 'trial' : null;
    default:
      return null;
  }
}

/** "5 days left in your free trial" / "1 day left…" / "Your free trial ends today". */
export function trialMessage(daysLeft: number | null): string {
  if (daysLeft === null) return 'You are on a free trial.';
  if (daysLeft <= 0) return 'Your free trial ends today.';
  return `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left in your free trial.`;
}

export interface SubscriptionBannerProps {
  subscription: AccountSubscription | null;
  /** Holds admin:subscription:manage (owner/admin): sees the card / plan / Undo actions. */
  canManage: boolean;
  timeZone?: string;
  /** Open Organization Account (plans). */
  onOpenAccount: () => void;
  /** Add or replace the card (Stripe Checkout in setup mode). */
  onPaymentMethod: () => void;
  /** Undo a scheduled cancellation. */
  onUndoCancel: () => void;
  busy?: boolean;
  error?: string | null;
  className?: string;
}

const buttonClass = (tone: 'sky' | 'amber' | 'red') =>
  `rounded px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 ${
    tone === 'sky' ? 'bg-sky-700 hover:bg-sky-800' : tone === 'amber' ? 'bg-amber-700 hover:bg-amber-800' : 'bg-red-700 hover:bg-red-800'
  }`;

/**
 * Trial / past-due / read-only / cancel-scheduled notice for the main layout (Phase 13, §4.1).
 * - trialing: info with days left (warning at ≤2); from day 10 (≤4 days left, ACC-08) managers
 *   without a card get "Add card"; nothing once a card is on file.
 * - past_due: warning, "Replace card" (setup mode; it pays the open invoice).
 * - read_only: error; managers get "Choose a plan".
 * - cancel scheduled: info with the end date; managers get Undo.
 */
const SubscriptionBanner: React.FC<SubscriptionBannerProps> = ({
  subscription,
  canManage,
  timeZone = DEFAULT_AGENCY_TIMEZONE,
  onOpenAccount,
  onPaymentMethod,
  onUndoCancel,
  busy = false,
  error = null,
  className = 'rounded-none border-x-0 border-t-0',
}) => {
  const kind = subscriptionBannerKind(subscription);
  if (!subscription || !kind) return null;
  const errorLine = error ? <p className="mt-1 text-xs font-medium" role="alert">{error}</p> : null;

  if (kind === 'read_only') {
    return (
      <Banner
        variant="error"
        data-testid="subscription-banner-read-only"
        className={className}
        action={canManage ? (
          <button type="button" onClick={onOpenAccount} className={buttonClass('red')}>Choose a plan</button>
        ) : undefined}
      >
        Your account is read-only. You can view and export data.
        {errorLine}
      </Banner>
    );
  }

  if (kind === 'past_due') {
    return (
      <Banner
        variant="warning"
        data-testid="subscription-banner-past-due"
        className={className}
        action={canManage ? (
          <button
            type="button"
            onClick={onPaymentMethod}
            disabled={busy || !subscription.billingEnabled}
            title={subscription.billingEnabled ? undefined : BILLING_DISABLED_TITLE}
            className={buttonClass('amber')}
          >
            Replace card
          </button>
        ) : undefined}
      >
        Payment failed — replace your card.
        {errorLine}
      </Banner>
    );
  }

  if (kind === 'cancel_scheduled') {
    const date = formatAccountDate(subscription.currentPeriodEnd ?? subscription.trialEndsAt, timeZone);
    return (
      <Banner
        variant="info"
        data-testid="subscription-banner-cancel"
        className={className}
        action={canManage ? (
          <button type="button" onClick={onUndoCancel} disabled={busy} className={buttonClass('sky')}>Undo</button>
        ) : undefined}
      >
        {date ? `Your subscription ends on ${date}.` : 'Your subscription ends at the end of this period.'}
        {errorLine}
      </Banner>
    );
  }

  // Trial
  const daysLeft = subscription.trialDaysLeft;
  const urgent = daysLeft !== null && daysLeft <= 2;
  const showAddCard = canManage && subscription.hasPaymentMethod !== true && daysLeft !== null && daysLeft <= TRIAL_UPGRADE_PROMPT_DAYS_LEFT;
  return (
    <Banner
      variant={urgent ? 'warning' : 'info'}
      data-testid="subscription-banner-trial"
      className={className}
      action={showAddCard ? (
        <button
          type="button"
          onClick={onPaymentMethod}
          disabled={busy || !subscription.billingEnabled}
          title={subscription.billingEnabled ? undefined : BILLING_DISABLED_TITLE}
          className={buttonClass(urgent ? 'amber' : 'sky')}
        >
          Add card
        </button>
      ) : undefined}
    >
      {trialMessage(daysLeft)}
      {errorLine}
    </Banner>
  );
};

export default SubscriptionBanner;
